import React, { createContext, useContext, useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { getCustomers } from '../../actions/ActionCustomer';

export const CustomerListContext = createContext({});

export const CustomerListProvider = ({ children }) => {
  const [aCustomers, setCustomers] = useState([]);
  const [bHasMorePages, setHasMorePages] = useState(true);
  const [bIsLoading, setLoading] = useState(false);

  const [nPage, setPage] = useState(0);
  const [oOrderBy, setOrderBy] = useState({ cColumn: 'cust_nid', cType: 'asc' });
  const [cSearch, setSearch] = useState('');
  const [nWlpnId, setWlpnId] = useState(null);

  const oObserverRef = useRef(null);
  const fnLastItemRef = useCallback((elLastItem) => {
    if (bIsLoading) return;
    if (oObserverRef.current) {
      oObserverRef.current.disconnect();
    }
    if (!bHasMorePages) return;

    oObserverRef.current = new IntersectionObserver(([oEntry]) => {
      if (!oEntry.isIntersecting) return;
      getNextPage();
    }, {
      threshold: 0,
      rootMargin: '0px 200px 0px 200px',
    });

    if (elLastItem) oObserverRef.current.observe(elLastItem);
  }, [bIsLoading, bHasMorePages]);

  const getNextPage = useCallback(async (bReset = false) => {
    // customer/subscription-change

    if ((!bReset && !bHasMorePages) || bIsLoading) return;

    setLoading(true);

    const nNextPage = bReset ? 1 : nPage + 1;
    const { cColumn, cType } = oOrderBy;
    const { aCustomers: aNewCustomers, bHasNextPage } = await getCustomers(nNextPage, cColumn, cType, cSearch, nWlpnId);

    setCustomers(aPrevCustomers => (bReset ? aNewCustomers : [...aPrevCustomers, ...aNewCustomers]));
    setHasMorePages(bHasNextPage);
    setPage(nNextPage);

    setLoading(false);
  }, [bHasMorePages, bIsLoading, nPage, oOrderBy, cSearch, nWlpnId]);

  const updateOrderBy = (cColumn) => {
    if (oOrderBy.cColumn === cColumn) {
      setOrderBy((oPrevOrderBy) => ({ cColumn, cType: (oPrevOrderBy.cType === 'asc' ? 'desc' : 'asc') }));
    } else {
      setOrderBy({ cColumn, cType: 'asc' });
    }
  };

  useEffect(() => {
    getNextPage(true);
  }, [nWlpnId, oOrderBy, cSearch]);

  const oValues = useMemo(
    () => ({
      aCustomers,
      bHasMorePages,
      nPage,
      cSearch,
      nWlpnId,
      oOrderBy,
      bIsLoading,
      fnLastItemRef,
      updateOrderBy,
      getNextPage,
      setSearch,
      setWlpnId,
    }),
    [aCustomers, bHasMorePages, nPage, cSearch, oOrderBy, bIsLoading, fnLastItemRef, updateOrderBy, setSearch, getNextPage]
  );

  return <CustomerListContext.Provider value={oValues}>{children}</CustomerListContext.Provider>;
};

export const useCustomerListContext = () => useContext(CustomerListContext);